import { useEffect, useMemo, useState } from 'react';
import { featureFlagsSchema, type AppSettings, type FeatureFlagKey, type FeatureFlags } from '@ace-tracker/shared';
import { TOOL_LABELS } from '../Sidebar';
import { Card, focusRing, SaveBar } from './formParts';
import { useSaveSettings } from './useSaveSettings';

const TOOLS: { key: FeatureFlagKey; label: string }[] = [
  { key: 'copilot', label: TOOL_LABELS.copilot },
  { key: 'claude-code', label: TOOL_LABELS['claude-code'] },
  { key: 'codex', label: TOOL_LABELS.codex },
  { key: 'kiro', label: TOOL_LABELS.kiro },
  { key: 'gemini', label: TOOL_LABELS.gemini },
  { key: 'cursor', label: `${TOOL_LABELS.cursor} (preview)` },
  { key: 'windsurf', label: `${TOOL_LABELS.windsurf} (preview, activity only)` },
];

const OPTIMIZERS: { key: FeatureFlagKey; label: string }[] = [
  { key: 'rtk', label: 'RTK' },
  { key: 'headroom', label: 'Headroom' },
  { key: 'graphify', label: 'Graphify' },
];

function Switch({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <li className="flex min-h-[44px] items-center justify-between gap-4">
      <span id={`${id}-label`} className="text-sm text-text-default">{label}</span>
      <span className="flex items-center gap-3">
        <span className="w-7 text-right text-xs font-medium text-text-muted">{checked ? 'On' : 'Off'}</span>
        <button
          type="button"
          role="switch"
          id={id}
          aria-checked={checked}
          aria-labelledby={`${id}-label`}
          onClick={() => onChange(!checked)}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${focusRing} ${checked ? 'bg-primary-blue' : 'bg-border'}`}
        >
          <span
            aria-hidden="true"
            className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-surface shadow-sm transition-transform duration-150 ${checked ? 'translate-x-5' : ''}`}
          />
        </button>
      </span>
    </li>
  );
}

/** Settings → Features: which tools and optimizers show in the sidebar for every user. */
export function FeatureFlagsSettings({ settings }: { settings: AppSettings }) {
  const baseline = settings.features;
  const [draft, setDraft] = useState<FeatureFlags>(baseline);
  const { save, saving, error, savedAt, reset } = useSaveSettings('features');
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

  useEffect(() => setDraft(baseline), [baseline]);

  const toggle = (key: FeatureFlagKey) => (next: boolean) => setDraft((d) => ({ ...d, [key]: next }));

  return (
    <div className="space-y-5">
      <Card
        title="Tools"
        description="Turn a tool off to remove it from the sidebar for every user. Its telemetry is still collected and still counts in All Tools and Executive."
      >
        <ul className="divide-y divide-border">
          {TOOLS.map((t) => (
            <Switch key={t.key} id={`feature-${t.key}`} label={t.label} checked={draft[t.key]} onChange={toggle(t.key)} />
          ))}
        </ul>
      </Card>

      <Card
        title="Optimizers"
        description="Turn an optimizer off to remove it from the sidebar. When all three are off, the Optimizers group and the Token Optimization overview are hidden too."
      >
        <ul className="divide-y divide-border">
          {OPTIMIZERS.map((t) => (
            <Switch key={t.key} id={`feature-${t.key}`} label={t.label} checked={draft[t.key]} onChange={toggle(t.key)} />
          ))}
        </ul>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={error}
        savedAt={savedAt}
        audit={settings.audit.features}
        onSave={() => save(featureFlagsSchema.parse(draft))}
        onDiscard={() => {
          setDraft(baseline);
          reset();
        }}
      />
    </div>
  );
}
